import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, FileText } from 'lucide-react';
import { CLUSTERS, ICON_COMPONENTS, clusterFromKicker } from '../insightsTaxonomy';
import { useArticles } from '../articlesApi';
import { useSEO } from '../hooks/useSEO';

/**
 * Topic cluster landing page, /insights/topic/:cluster
 *
 * Groups every article whose kicker matches the cluster into a single page.
 * Boosts topical authority signals to Google/AI models by:
 *   - Sitting each article under a keyword-heavy H1
 *   - Cross-linking every article in the cluster from one URL
 *   - Emitting an ItemList JSON-LD block that names all the articles
 */

const V2ResourceCluster = () => {
  const { cluster } = useParams();
  const meta = CLUSTERS[cluster];
  const { articles: allArticles, loading, error } = useArticles();

  useSEO({
    title: meta
      ? `${meta.title}, Keystone Resources`
      : 'Resources, Keystone Strategic Consultants',
    description: meta ? meta.description : 'Practitioner-led insights from Keystone Strategic Consultants.',
    path: `/insights/topic/${cluster || ''}`,
  });

  if (!meta) return <Navigate to="/insights" replace />;

  const articles = allArticles.filter((a) => clusterFromKicker(a.kicker) === cluster);
  const Icon = meta.icon;

  return (
    <div className="bg-white text-slate-900" data-testid={`v2-resource-cluster-${cluster}`}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: meta.title,
            description: meta.description,
            itemListElement: articles.map((a, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `https://keystoneconsulting.uk/insights/${a.slug}`,
              name: a.title,
            })),
          }),
        }}
      />

      {/* Hero */}
      <section className="pt-14 md:pt-20 pb-14 border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/insights"
            className="inline-flex items-center text-sm text-slate-600 hover:text-blue-700 transition-colors mb-8"
            data-testid="v2-resource-cluster-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            All insights
          </Link>
          <div className="flex items-start gap-4 max-w-3xl">
            <span className={`w-12 h-12 rounded-lg border flex items-center justify-center flex-shrink-0 ${meta.accent}`}>
              <Icon className="w-5 h-5" />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">
                Topic · {meta.label}
              </p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1] mb-5">
                {meta.title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                {meta.description}
              </p>
              <p className="mt-5 text-sm text-slate-500">
                {loading
                  ? 'Loading…'
                  : `${articles.length} article${articles.length === 1 ? '' : 's'} in this cluster.`}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Article grid */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <p className="text-slate-500 text-center py-10">Loading articles…</p>
          )}
          {error && !loading && (
            <div className="text-center py-10" data-testid="v2-cluster-error">
              <p className="text-slate-600 mb-4">We had trouble loading the articles just now.</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-blue-500 hover:text-blue-700 text-slate-700 rounded-full px-5 py-2 text-sm transition-colors"
                data-testid="v2-cluster-retry-btn"
              >
                Try again
              </button>
            </div>
          )}
          {!loading && !error && articles.length === 0 ? (
            <p className="text-slate-500 text-center py-10">
              No articles in this cluster yet, check back next week.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {articles.map((a) => {
                const CardIcon = ICON_COMPONENTS[a.icon] || FileText;
                return (
                  <Link
                    to={`/insights/${a.slug}`}
                    key={a.slug}
                    className="group border border-slate-200 rounded-xl p-7 md:p-8 hover:border-blue-500 hover:shadow-lg transition-all bg-white block"
                    data-testid={`v2-resource-cluster-card-${a.slug}`}
                  >
                    <div className="flex items-center justify-between mb-5">
                      <span className={`w-11 h-11 rounded-lg border flex items-center justify-center ${meta.accent}`}>
                        <CardIcon className="w-5 h-5" />
                      </span>
                      <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold inline-flex items-center gap-1.5">
                        <Clock className="w-3 h-3" /> {a.readMinutes} min
                      </span>
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-blue-600 font-semibold mb-3">
                      {a.kicker}
                    </p>
                    <h2 className="text-xl md:text-2xl font-medium text-slate-900 mb-4 leading-tight group-hover:text-blue-700 transition-colors">
                      {a.title}
                    </h2>
                    <p className="text-[14.5px] text-slate-600 leading-relaxed mb-5">
                      {a.excerpt}
                    </p>
                    <span className="inline-flex items-center text-sm font-medium text-blue-600 group-hover:text-blue-700">
                      Read the full note
                      <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Other clusters */}
      <section className="py-14 border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-6">
            Other topics
          </p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(CLUSTERS)
              .filter(([slug]) => slug !== cluster)
              .map(([slug, c]) => (
                <Link
                  key={slug}
                  to={`/insights/topic/${slug}`}
                  className="inline-flex items-center gap-2 border border-slate-300 hover:border-blue-500 hover:text-blue-700 bg-white text-slate-700 rounded-full px-4 py-2 text-sm transition-colors"
                  data-testid={`v2-resource-cluster-link-${slug}`}
                >
                  <c.icon className="w-3.5 h-3.5" /> {c.label}
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default V2ResourceCluster;
